import { CloseIcon, UploadIcon } from '@/assets/icons';
import {
  ACCEPT_TYPES,
  MAX_COMPRESSED_SIZE,
  MAX_FILE_SIZE,
  REQUIRED_HEIGHT,
  REQUIRED_WIDTH,
} from '@/data/constants';
import { validateNricDocument } from '@/data/apis/postDocumentValidation';
import { useAppDispatch } from '@/data/reducers';
import { NS_APPLICATION } from '@/i18n/namespaces';
import { Input } from '@headlessui/react';
import {
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import styled, { keyframes } from 'styled-components';
import { twMerge } from 'tailwind-merge';

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const getBase64SizeInBytes = (base64: string): number => {
  const base64Only = base64.split(',')[1] || base64;

  return Math.ceil(
    base64Only.length * (3 / 4) -
      (base64Only.endsWith('==')
        ? 2
        : base64Only.endsWith('=')
          ? 1
          : 0),
  );
};

const compressBase64ToUnderSize = async (
  base64Input: string,
  maxSizeInBytes: number,
  minQuality = 0.6,
): Promise<string | null> => {
  const originalSize = getBase64SizeInBytes(base64Input);

  // Skip compression if already below limit
  if (originalSize <= maxSizeInBytes) {
    return base64Input;
  }

  const image = new Image();
  image.src = base64Input;

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () =>
      reject(new Error('Failed to load image.'));
  });

  let targetW = image.naturalWidth;
  let targetH = image.naturalHeight;

  if (
    targetW > REQUIRED_WIDTH ||
    targetH > REQUIRED_HEIGHT
  ) {
    const scale = Math.min(
      REQUIRED_WIDTH / targetW,
      REQUIRED_HEIGHT / targetH,
    );

    targetW = Math.round(targetW * scale);
    targetH = Math.round(targetH * scale);
  }

  const canvas = document.createElement('canvas');

  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  ctx.drawImage(image, 0, 0, targetW, targetH);

  let quality = 0.92;

  while (quality >= minQuality) {
    const compressed = canvas.toDataURL(
      'image/jpeg',
      quality,
    );

    const compressedSize =
      getBase64SizeInBytes(compressed);

    if (compressedSize <= maxSizeInBytes) {
      return compressed;
    }

    quality -= 0.05;
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Styled-components                                                  */
/* ------------------------------------------------------------------ */

const CameraOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #000;
  display: flex;
  flex-direction: column;
`;

const CameraVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

const GuideLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const GuideRect = styled.div`
  width: 85%;
  aspect-ratio: 85.6 / 53.98;
  border: 3px solid #22d3ee;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
`;

const GuideText = styled.div`
  position: absolute;
  top: calc(50% + 20%);
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  width: 90%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #fff;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ShutterWrapper = styled.div`
  position: absolute;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;

const ShutterButton = styled.button<{ $disabled?: boolean }>`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${({
    $disabled,
  }: {
    $disabled?: boolean;
  }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({
    $disabled,
  }: {
    $disabled?: boolean;
  }) =>
    $disabled ? 'none' : 'auto'};
`;

const ShutterInner = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #fff;
`;

const spinAnimation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ProcessingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spinAnimation} 0.8s linear infinite;
`;

const ProcessingText = styled.span`
  color: #fff;
  font-size: 14px;
`;

/* ------------------------------------------------------------------ */
/* Validation Helpers                                                 */
/* ------------------------------------------------------------------ */

const normalizeCardSide = (
  value?: string,
): 'front' | 'back' | '' => {
  if (!value) return '';

  const v = value.trim().toLowerCase();

  if (v.includes('front')) return 'front';
  if (v.includes('back')) return 'back';

  return '';
};

const getValidationErrorMessage = (
  validationResult: {
    status: string;
    card_side?: string;
    is_readable?: boolean;
    is_nric_document?: boolean;
  },
  expectedSide?: 'front' | 'back',
): string | null => {
  const status =
    validationResult.status?.toUpperCase();

  const isReadable =
    validationResult.is_readable;

  const isNricDocument =
    validationResult.is_nric_document;

  const actualSide = normalizeCardSide(
    validationResult.card_side,
  );

  const isCardSideMatched =
    !expectedSide ||
    actualSide === expectedSide;

  if (
    status === 'ACCEPTED' &&
    isCardSideMatched &&
    isReadable === true &&
    isNricDocument === true
  ) {
    return null;
  }

  if (
    status === 'ACCEPTED' &&
    !isCardSideMatched
  ) {
    return 'label.document.error.wrongSide';
  }

  if (status === 'REJECTED') {
    return 'label.document.error.rejected';
  }

  if (
    status === 'UNCLEAR' &&
    isReadable === false &&
    isNricDocument === false
  ) {
    return 'label.document.error.rejected';
  }

  if (
    status === 'UNCLEAR' &&
    isReadable === false &&
    isNricDocument === true
  ) {
    return 'label.document.error.imageUnclear';
  }

  return 'label.document.error.rejected';
};

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

type UploadDocumentProps = {
  label?: string;
  error?: string;
  id: string;
  onChange: (base64: string | null) => void;
  value?: string;
  itemRef?: Ref<HTMLInputElement>;
  accept: string;
  maxSize: number;
  enableValidation?: boolean;
  side?: 'front' | 'back';
};

export const UploadDocument = ({
  label,
  error,
  id,
  itemRef,
  onChange,
  value,
  accept,
  maxSize,
  enableValidation = false,
  side,
}: UploadDocumentProps) => {
  const [isDragging, setIsDragging] =
    useState(false);

  const [previewUrl, setPreviewUrl] =
    useState<string | null>(value ?? null);

  const [validationError, setValidationError] =
    useState<string | null>(null);

  const [isValidating, setIsValidating] =
    useState(false);

  const [cameraOpen, setCameraOpen] =
    useState(false);

  const [isProcessing, setIsProcessing] =
    useState(false);

  const pendingStreamRef =
    useRef<MediaStream | null>(null);

  const videoElRef =
    useRef<HTMLVideoElement | null>(null);

  const dispatch = useAppDispatch();

  const { t } = useTranslation(
    NS_APPLICATION,
    {
      keyPrefix: 'personalDetailForm',
    },
  );

  /* -------------------------------------------------------------- */
  /* Validation                                                     */
  /* -------------------------------------------------------------- */

  const runValidation = async (
    rawBase64: string,
  ) => {
    if (!enableValidation) return;

    setValidationError(null);
    setIsValidating(true);

    try {
      const validationResult =
        await dispatch(
          validateNricDocument({
            base64Image: rawBase64,
            side,
          }),
        ).unwrap();

      const errorMessage =
        getValidationErrorMessage(
          validationResult,
          side,
        );

      if (errorMessage) {
        setValidationError(errorMessage);
        onChange(null);
        setPreviewUrl(null);
      }
    } catch {
      // Allow upload if validation API fails
    } finally {
      setIsValidating(false);
    }
  };

  /* -------------------------------------------------------------- */
  /* Desktop Upload                                                 */
  /* -------------------------------------------------------------- */

  const onUpload = async (
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) {
      onChange(null);
      setPreviewUrl(null);
      return;
    }

    const file = files[0];

    const fileTypes =
      ACCEPT_TYPES.split(',');

    if (
      !fileTypes.includes(file.type) ||
      file.size > MAX_FILE_SIZE
    ) {
      const reader = new FileReader();

      reader.onloadend = () => {
        onChange(reader.result as string);
        setPreviewUrl(null);
      };

      reader.readAsDataURL(file);

      return;
    }

    const reader = new FileReader();

    reader.onloadend = async () => {
      let base64Image =
        reader.result as string;

      const compressed =
        await compressBase64ToUnderSize(
          base64Image,
          MAX_COMPRESSED_SIZE,
        );

      if (compressed) {
        base64Image = compressed;
      }

      setPreviewUrl(base64Image);

      onChange(base64Image);

      await runValidation(base64Image);
    };

    reader.readAsDataURL(file);
  };

  /* -------------------------------------------------------------- */
  /* Camera Helpers                                                 */
  /* -------------------------------------------------------------- */

  const stopCamera = useCallback(() => {
    pendingStreamRef.current
      ?.getTracks()
      .forEach((t) => t.stop());

    pendingStreamRef.current = null;
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setCameraOpen(false);
    setIsProcessing(false);
  }, [stopCamera]);

  const openCamera = useCallback(async () => {
    try {
      const stream =
        await navigator.mediaDevices.getUserMedia(
          {
            video: {
              facingMode: {
                ideal: 'environment',
              },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
          },
        );

      pendingStreamRef.current = stream;

      setCameraOpen(true);
    } catch (err) {
      console.error(
        'Camera access denied',
        err,
      );
    }
  }, []);

  const videoRefCallback = useCallback(
    (el: HTMLVideoElement | null) => {
      videoElRef.current = el;

      if (
        el &&
        pendingStreamRef.current
      ) {
        el.srcObject =
          pendingStreamRef.current;

        el.play().catch(() => {});
      }
    },
    [],
  );

  /* -------------------------------------------------------------- */
  /* Mobile Capture                                                 */
  /* -------------------------------------------------------------- */

  const handleShutter = useCallback(
    async () => {
      const video = videoElRef.current;

      if (!video) return;

      setIsProcessing(true);

      try {
        const videoW =
          video.videoWidth;

        const videoH =
          video.videoHeight;

        if (!videoW || !videoH) {
          closeCamera();
          return;
        }

        /**
         * Malaysian NRIC ratio
         */
        const cardRatio =
          85.6 / 53.98;

        /**
         * Safe center crop
         */
        let cropW = videoW * 0.85;

        let cropH =
          cropW / cardRatio;

        if (
          cropH >
          videoH * 0.85
        ) {
          cropH =
            videoH * 0.85;

          cropW =
            cropH * cardRatio;
        }

        const cropX =
          (videoW - cropW) / 2;

        const cropY =
          (videoH - cropH) / 2;

        const canvas =
          document.createElement(
            'canvas',
          );

        canvas.width = cropW;
        canvas.height = cropH;

        const ctx =
          canvas.getContext('2d');

        if (!ctx) {
          closeCamera();
          return;
        }

        ctx.drawImage(
          video,
          cropX,
          cropY,
          cropW,
          cropH,
          0,
          0,
          cropW,
          cropH,
        );

        let base64Image =
          canvas.toDataURL(
            'image/jpeg',
            0.95,
          );

        const compressed =
          await compressBase64ToUnderSize(
            base64Image,
            MAX_COMPRESSED_SIZE,
          );

        if (compressed) {
          base64Image =
            compressed;
        }

        stopCamera();

        setPreviewUrl(base64Image);

        onChange(base64Image);

        setCameraOpen(false);
        setIsProcessing(false);

        await runValidation(
          base64Image,
        );
      } catch (err) {
        console.error(
          'Capture failed',
          err,
        );

        closeCamera();
      }
    },
    [
      onChange,
      closeCamera,
      runValidation,
      stopCamera,
    ],
  );

  /* -------------------------------------------------------------- */
  /* Common Handlers                                                */
  /* -------------------------------------------------------------- */

  const onRemove = (
    e: React.MouseEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    onChange(null);

    setPreviewUrl(null);

    setValidationError(null);

    if (
      itemRef &&
      'current' in itemRef &&
      itemRef.current
    ) {
      itemRef.current.value = '';
    }
  };

  const handleDragOver = (
    e: React.DragEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (
    e: React.DragEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (
    e: React.DragEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);

    onUpload(e.dataTransfer.files);
  };

  const openFilePicker = () => {
    const inputEl = document.getElementById(
      id,
    ) as HTMLInputElement | null;

    inputEl?.click();
  };

  const handleUploadClick = (
    e: React.MouseEvent,
  ) => {
    e.preventDefault();

    if (isMobile) {
      if (isValidating || isProcessing) return;

      openCamera();
      return;
    }

    openFilePicker();
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  /* -------------------------------------------------------------- */
  /* Render                                                         */
  /* -------------------------------------------------------------- */

  return (
    <div className="flex-1 space-y-2" data-error-anchor={id}>
      {label && (
        <span
          className={twMerge(
            'block text-sm font-medium text-gray-700',
            error &&
              'text-red-500',
          )}
        >
          {label}
        </span>
      )}

      <Input
        ref={itemRef}
        type="file"
        id={id}
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const files =
            e.target.files;

          if (
            !files ||
            files.length === 0
          ) {
            return;
          }

          onUpload(files);

          e.target.value = '';
        }}
      />

      {isMobile ? (
        <>
          <label
            htmlFor={undefined}
            role="button"
            tabIndex={0}
            onClick={handleUploadClick}
            className={twMerge(
              'relative flex h-[200px] cursor-pointer items-center justify-center rounded-[2px] border border-dashed bg-[#D9D9D9] px-4 text-center',
              (error || validationError)
                ? 'border-[#FF7070]'
                : 'border-[#BDBDBD]',
            )}
          >
            {previewUrl && (
              <button
                className="absolute right-2 top-2 rounded-full bg-white shadow-sm hover:bg-gray-100"
                aria-label="Remove file"
                onClick={onRemove}
              >
                <CloseIcon className="size-5 text-gray-500" />
              </button>
            )}

{isValidating ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <div className="size-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" />
                <span className="text-sm text-gray-500">
                  {t('label.document.validating')}
                </span>
              </div>
            ) : previewUrl ? (
              <div className="flex flex-col items-center">
                {previewUrl.startsWith('data:image') ? (
                  <img
                    className="h-full w-full object-contain"
                    src={previewUrl}
                    alt="Preview"
                  />
                ) : (
                  <div className="mb-2 flex size-20 items-center justify-center rounded bg-gray-200">
                    <span className="text-xs">{t('label.document.fileUploaded')}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadIcon className="size-8 text-[#222222]" />
                <span className="font-bold text-[#1293EB]">
                  {t('label.document.click to upload')}
                </span>
              </div>
            )}
          </label>

          {(error || validationError) && (
            <span className="text-xs text-[#FF4D4F]">
              {validationError ? String(t(validationError as any)) : error}
            </span>
          )}
        </>
      ) : (
        <>
          <label
            htmlFor={id}
            onClick={handleUploadClick}
            className={twMerge(
              'relative block cursor-pointer rounded-sm border-2 border-dashed p-6 text-center transition-colors',
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-gray-50',
              (error || validationError) &&
                'border-red-300 bg-red-50',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {previewUrl && (
              <button
                className="absolute right-0 top-0 rounded-full bg-white shadow-sm hover:bg-gray-100"
                aria-label="Remove file"
                onClick={onRemove}
              >
                <CloseIcon className="size-5 text-gray-500" />
              </button>
            )}

            {isValidating ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <div className="size-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" />
                <span className="text-sm text-gray-500">
                  {t('label.document.validating')}
                </span>
              </div>
            ) : previewUrl ? (
              <div className="flex flex-col items-center">
                {previewUrl.startsWith('data:image') ? (
                  <img
                    className="max-h-[220px] max-w-[392px] object-contain"
                    src={previewUrl}
                    alt="Preview"
                  />
                ) : (
                  <div className="mb-2 flex size-20 items-center justify-center rounded bg-gray-200">
                    <span className="text-xs">{t('label.document.fileUploaded')}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadIcon className="size-8 text-gray-400" />

                <span className="font-bold">
                  {t('label.document.Drag and drop or')}
                </span>

                <span className="font-bold text-[#0EA5E9]">
                  {t('label.document.click to upload')}
                </span>

                <span className="text-sm text-secondary">
                  {t('label.document.max')} {maxSize / 1000000}MB ({accept})
                </span>
              </div>
            )}
          </label>

          {(error || validationError) && (
            <span className="text-sm text-red-500">
              {validationError ? String(t(validationError as any)) : error}
            </span>
          )}
        </>
      )}

      {/* Mobile Camera Overlay */}

      {cameraOpen &&
        createPortal(
          <CameraOverlay>
            <CameraVideo
              ref={
                videoRefCallback
              }
              autoPlay
              playsInline
              muted
            />

            <GuideLayer>
              <GuideRect />
              <GuideText>
                {side === 'front'
                  ? t('label.document.nricFrontGuide')
                  : t('label.document.nricBackGuide')}
              </GuideText>
            </GuideLayer>

            <CloseButton
              onClick={
                closeCamera
              }
              aria-label="Close camera"
            >
              ✕
            </CloseButton>

            <ShutterWrapper>
              <ShutterButton
                $disabled={
                  isProcessing
                }
                onClick={
                  handleShutter
                }
                aria-label="Capture"
              >
                <ShutterInner />
              </ShutterButton>
            </ShutterWrapper>

            {isProcessing && (
              <ProcessingOverlay>
                <Spinner />
                <ProcessingText>
                  {t('label.document.processing')}
                </ProcessingText>
              </ProcessingOverlay>
            )}
          </CameraOverlay>,
          document.body,
        )}
    </div>
  );
};
